import PropTypes from "prop-types";
import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";

import { styled, useTheme } from "@mui/material/styles";
import {
  Box,
  ClickAwayListener,
  Collapse,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Paper,
  Popper,
  Typography,
  useMediaQuery,
} from "@mui/material";

import { NavItem } from "./NavItem";
import Transitions from "ui-component/extended/Transitions";

import { useSelector } from "store";
import LAYOUT_CONST from "constant";
import useConfig from "hooks/useConfig";
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded';
import ExpandLessRoundedIcon from '@mui/icons-material/ExpandLessRounded';
import ChevronRightRoundedIcon from '@mui/icons-material/ChevronRightRounded';
import FiberManualRecordIcon from "@mui/icons-material/FiberManualRecord";

// mini-menu - wrapper
const PopperStyledMini = styled(Popper)(({ theme }) => ({
  overflow: "visible",
  zIndex: 1202,
  minWidth: 180,
  "&:before": {
    content: '""',
    backgroundColor: theme.palette.background.paper,
    transform: "translateY(-50%) rotate(45deg)",
    zIndex: 120,
    borderLeft: `1px solid ${theme.palette.grey.A800}`,
    borderBottom: `1px solid ${theme.palette.grey.A800}`,
  },
}));

// horizontal-menu - wrapper
const PopperStyled = styled(Popper)(({ theme }) => ({
  overflow: "visible",
  zIndex: 1202,
  minWidth: 180,
  "&:before": {
    content: '""',
    display: "block",
    position: "absolute",
    top: 34,
    left: -5,
    width: 12,
    height: 12,
    transform: "translateY(-50%) rotate(45deg)",
    zIndex: 120,
    borderWidth: "6px",
    borderStyle: "solid",
    borderColor: `transparent transparent ${theme.palette.background.paper}  ${theme.palette.background.paper}`,
  },
  '&[data-popper-placement="left-start"]:before': {
    left: "auto",
    right: -5,
    borderColor: `${theme.palette.background.paper}  ${theme.palette.background.paper} transparent transparent`,
  },
  '&[data-popper-placement="left-end"]:before': {
    top: "auto",
    bottom: 15,
    left: "auto",
    right: -5,
    borderColor: `${theme.palette.background.paper}  ${theme.palette.background.paper} transparent transparent`,
  },
  '&[data-popper-placement="right-end"]:before': {
    top: "auto",
    bottom: 15,
  },
}));

// ==============================|| SIDEBAR MENU LIST COLLAPSE ITEMS ||============================== //

export const NavCollapse = ({ menu, level, parentId, className }) => {
  const theme = useTheme();
  const matchDownMd = useMediaQuery(theme.breakpoints.down("md"));

  const { layout, /*borderRadius*/ } = useConfig();
  const { drawerOpen } = useSelector((state) => state.menu);

  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);

  const handleClickMini = (event) => {
    setAnchorEl(null);
    if (drawerOpen) {
      setOpen(!open);
    } else {
      setAnchorEl(event?.currentTarget);
    }
  };

  const handleHover = (event) => {
    setAnchorEl(event?.currentTarget);
  };

  const handleClosePopper = () => {
    setOpen(false);
    setSelected(null);
    setAnchorEl(null);
  };

  const openMini = Boolean(anchorEl);
  const { pathname } = useLocation();

  const checkOpenForParent = (child, id) => {
    child.forEach((item) => {
      if (item.url === pathname) {
        setOpen(true);
        setSelected(id);
      }
    });
  };

  // menu collapse for sub-levels
  useEffect(() => {
    setOpen(false);
    setSelected(null);
    if (openMini) setAnchorEl(null);
    if (menu.children) {
      menu.children.forEach((item) => {
        if (item.children?.length) {
          checkOpenForParent(item.children, menu.id);
        }

        if (pathname.includes(menu.id)) {
          setSelected(menu.id);
          setOpen(true);
        }
      });
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname, menu.children]);

  // menu collapse & item
  const menus = menu.children?.map((item) => {
    switch (item.type) {
      case "collapse":
        return (
          <NavCollapse
            key={item.id}
            menu={item}
            level={level + 1}
            parentId={menu.id}
            className={item.className}
          />
        );
      case "item":
        return (
          <NavItem
            key={item.id}
            item={item}
            level={level + 1}
            parentId={menu.id}
            className={item.className}
          />
        );
      default:
        return (
          <Typography key={item.id} variant="h6" color="error" align="center">
            Menu Items Error
          </Typography>
        );
    }
  });

  const isSelected = selected === menu.id;

  const Icon = menu.icon;
  const menuIcon = menu.icon ? (
    <Icon
      strokeWidth={1.5}
      size={drawerOpen ? "20px" : "24px"}
      style={{
        color: theme.palette.primary.main
      }}
    />
  ) : (
    <FiberManualRecordIcon
      sx={{
        color: theme.palette.mode === "dark"
          ? theme.palette.grey[400]
          : theme.palette.primary.main,
        width: isSelected ? 8 : 6,
        height: isSelected ? 8 : 6,
      }}
      fontSize={level > 0 ? "inherit" : "medium"}
    />
  );

  const collapseIcon = drawerOpen ? (
    <ExpandLessRoundedIcon
      stroke={1.5}
      sx={{ fontSize: "12px", mt: "auto", mb: "auto" }}
    />
  ) : (
    <ChevronRightRoundedIcon
      stroke={1.5}
      sx={{ fontSize: "12px", mt: "auto", mb: "auto", color: "text.primary" }}
    />
  );

  const popperId = openMini ? `collapse-pop-${menu.id}` : undefined;

  return (
    <div className={className}>
      {layout === LAYOUT_CONST.VERTICAL_LAYOUT ||
      (layout === LAYOUT_CONST.HORIZONTAL_LAYOUT && matchDownMd) ? (
        <>
          <ListItemButton
            sx={{
              zIndex: 1201,
              borderRadius: `4px`,
              mb: 0.5,
              px: "10px",
              pr: "10px",
              pl: `${level * 10}px`,
              // pl: drawerOpen ? `${level * 24}px` : 1.25,
              color: theme.palette.text.primary,
              ...(drawerOpen &&
                level === 1 && (
                theme.palette.mode === "light" 
                  ? {
                      "@media (hover: hover)": {
                        "&:hover": {
                          background: "#F9F9F9",
                          color: "text.primary",
                        },
                      },
                      "&.Mui-selected": {
                        background: theme.palette.secondary.light,
                        color: theme.palette.primary.main,
                        "@media (hover: hover)": {
                          "&:hover": {
                            color: theme.palette.primary.main,
                            background: theme.palette.secondary.light,
                          },
                        },
                      },
                    }
                  : {
                      "@media (hover: hover)": {
                        "&:hover": {
                          background: theme.palette.grey[100],
                          color: theme.palette.text.primary,
                        },
                      },
                      "&.Mui-selected": {
                        background: theme.palette.grey[200],
                        color: theme.palette.primary.main,
                        "@media (hover: hover)": {
                          "&:hover": {
                            background: theme.palette.grey[200],
                            color: theme.palette.primary.main,
                          },
                        },
                      },
                    }
                  )
                ),
              ...((!drawerOpen || level !== 1) && {
                py: level === 1 ? 0 : 1,
                ...(theme.palette.mode === "dark" ? {
                  "@media (hover: hover)": {
                    "&:hover": {
                      bgcolor: !drawerOpen && level === 1 ? "transparent" : theme.palette.grey[100],
                      color: theme.palette.text.primary,
                    },
                  },
                  "&.Mui-selected": {
                    bgcolor: !drawerOpen && level === 1 ? "transparent" : theme.palette.grey[200],
                    color: theme.palette.primary.main,
                    "@media (hover: hover)": {
                      "&:hover": {
                        bgcolor: !drawerOpen && level === 1 ? "transparent" : theme.palette.grey[200],
                        color: theme.palette.primary.main,
                      },
                    },
                  },
                } : {
                  "@media (hover: hover)": {
                    "&:hover": {
                      bgcolor: !drawerOpen && level === 1 ? "transparent" : "#F9F9F9",
                      color: theme.palette.primary.main,
                    },
                  },
                  "&.Mui-selected": {
                    bgcolor: !drawerOpen && level === 1 ? "transparent" : theme.palette.secondary.light,
                    "@media (hover: hover)": {
                      "&:hover": {
                        bgcolor: !drawerOpen && level === 1 ? "transparent" : theme.palette.secondary.light,
                      },
                    },
                  },
                }),
              }),
            }}
            selected={false}
            {...(!drawerOpen && {
              onMouseEnter: handleClickMini,
              onMouseLeave: handleClosePopper,
            })}
            onClick={handleClickMini}
          >
            {menuIcon && (
              <ListItemIcon
                sx={{
                  minWidth: level === 1 ? 36 : 18,
                  color: theme.palette.primary.main,
                  ...(!drawerOpen &&
                    level === 1 && {
                      borderRadius: `4px`,
                      width: 46,
                      height: 46,
                      alignItems: "center",
                      justifyContent: "center",
                      "@media (hover: hover)": {
                        "&:hover": {
                          bgcolor:
                            theme.palette.mode === "dark"
                              ? theme.palette.grey[100]
                              : "#F9F9F9",
                        },
                      },
                      ...(isSelected && {
                        bgcolor:
                          theme.palette.mode === "dark"
                            ? theme.palette.grey[200]
                            : "secondary.light",
                        "@media (hover: hover)": {
                          "&:hover": {
                            bgcolor:
                              theme.palette.mode === "dark"
                                ? theme.palette.grey[200]
                                : "secondary.light",
                          },
                        },
                      }),
                    }),
                }}
              >
                {menuIcon}
              </ListItemIcon>
            )}
            {(drawerOpen || (!drawerOpen && level !== 1)) && (
              <ListItemText
                primary={
                  <Typography
                    variant={selected === menu.id ? "h6" : "body1"}
                    color="inherit"
                    sx={{ my: "auto" }}
                  >
                    {menu.title}
                  </Typography>
                }
                secondary={
                  menu.caption && (
                    <Typography
                      variant="caption"
                      gutterBottom
                      sx={{
                        display: "block",
                        ...theme.typography.subMenuCaption
                      }}>
                      {menu.caption}
                    </Typography>
                  )
                }
              />
            )}

            {openMini || open ? (
              collapseIcon
            ) : (
              <ExpandMoreRoundedIcon
                stroke={1.5}
                sx={{ fontSize: "12px", mt: "auto", mb: "auto" }}
              />
            )}

            {!drawerOpen && (
              <PopperStyledMini
                open={openMini}
                anchorEl={anchorEl}
                placement="right-start"
                style={{
                  zIndex: 2001,
                }}
                modifiers={[
                  {
                    name: "offset",
                    options: {
                      offset: [-12, 0],
                    },
                  },
                ]}
              >
                {({ TransitionProps }) => (
                  <Transitions in={openMini} {...TransitionProps}>
                    <Paper
                      sx={{
                        overflow: "hidden",
                        mt: 1.5,
                        boxShadow: theme.shadows[8],
                        backgroundImage: "none",
                      }}
                    >
                      <ClickAwayListener onClickAway={handleClosePopper}>
                        <Box>{menus}</Box>
                      </ClickAwayListener>
                    </Paper>
                  </Transitions>
                )}
              </PopperStyledMini>
            )}
          </ListItemButton>
          {drawerOpen && (
            <Collapse in={open} timeout="auto" unmountOnExit>
              {open && (
                <List
                  component="div"
                  disablePadding
                >
                  {menus}
                </List>
              )}
            </Collapse>
          )}
        </>
      ) : null}
      {layout === LAYOUT_CONST.HORIZONTAL_LAYOUT && !matchDownMd ? (
        <>
          <ListItemButton
            id={`boundary-${popperId}`}
            disableRipple
            selected={false}
            onMouseEnter={handleHover}
            onMouseLeave={handleClosePopper}
            onClick={handleHover}
            aria-describedby={popperId}
          >
            {menuIcon && (
              <ListItemIcon
                sx={{
                  my: "auto",
                  minWidth: !menu.icon ? 18 : 36,
                  color: theme.palette.primary.main,
                }}
              >
                {menuIcon}
              </ListItemIcon>
            )}
            <ListItemText
              primary={
                <Typography
                  variant={selected === menu.id ? "h6" : "body1"}
                  color="inherit"
                  sx={{ my: "auto" }}
                >
                  {menu.title}
                </Typography>
              }
            />
            {openMini ? (
              <ChevronRightRoundedIcon stroke={1.5} sx={{ fontSize: "12px" }} />
            ) : (
              <ExpandMoreRoundedIcon stroke={1.5} sx={{ fontSize: "12px" }} />
            )}

            {anchorEl && (
              <PopperStyled
                id={popperId}
                open={openMini}
                anchorEl={anchorEl}
                placement="right-start"
                style={{
                  zIndex: 2001,
                }}
                modifiers={[
                  {
                    name: "offset",
                    options: {
                      offset: [-10, 0],
                    },
                  },
                ]}
              >
                {({ TransitionProps }) => (
                  <Transitions in={openMini} {...TransitionProps}>
                    <Paper
                      sx={{
                        overflow: "hidden",
                        mt: 1.5,
                        py: 0.5,
                        boxShadow: theme.shadows[8],
                        backgroundImage: "none",
                      }}
                    >
                      <ClickAwayListener onClickAway={handleClosePopper}>
                        <Box>{menus}</Box>
                      </ClickAwayListener>
                    </Paper>
                  </Transitions>
                )}
              </PopperStyled>
            )}
          </ListItemButton>
        </>
      ) : null}
    </div>
  );
};

NavCollapse.propTypes = {
  menu: PropTypes.object,
  level: PropTypes.number,
  parentId: PropTypes.string,
};
